import {useCallback, useEffect, useRef, useState} from 'react';
import Select, {components} from 'react-select';
import {useOptimizedSearch} from '../hooks/useOptimizedSearch';
import {asphalt, tanzanite} from '../sass/base/_exports.sass';
import {PencilIcon} from '../svg/PencilIcon';
import {XIcon} from '../svg/v2/XIcon';
import './AddressSelect.sass';

const DEBOUNCE_MS = 600;
const THROTTLE_MS = 1000;

const CustomInput = (props) => (
  <components.Input
    {...props}
    data-qa-id="event-location"
    aria-label="event-location"
    placeholder="Enter address, location name, or URL"
  />
);

const CustomControl = (props) => (
  <components.Control {...props} className="input-box address-select__topspace" />
);

const CustomOption = (props) => {
  const {data, getStyles, innerProps} = props;
  if (data.type === 'separator') {
    return <div className="address-select__separator" />;
  }
  const styles = getStyles('option', props);
  delete styles.padding;
  if (data.type === 'default') {
    return (
      <div {...innerProps} style={styles} className="address-select__option">
        <PencilIcon ratio={0.5} color={tanzanite} /> Use <b>&quot;{data.label}&quot;</b>
      </div>
    );
  }

  return (
    <div {...innerProps} style={styles} className="address-select__option">
      <div className="address-select__bold">{data.name}</div>
      <div className="address-select__gray">{data.address}</div>
    </div>
  );
};

const CustomClearIndicator = (props) => {
  const {
    children = <XIcon ratio={0.5} color={asphalt} />,
    getStyles,
    innerProps: {ref, ...restInnerProps},
    selectProps: {inputValue},
  } = props;
  return inputValue ? (
    <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
      <div className="address-select__clear" style={{marginRight: '0.5rem'}}>
        {children}
      </div>
    </div>
  ) : null;
};

// Fix for EVT-34293, makes input take entire width
const customStyles = {
  input: (baseStyles) => ({
    ...baseStyles,
    gridTemplateColumns: 'min-content',
  }),
};

const NoOptions = () => <div>Loading...</div>;

const AddressSelectComponents = {
  DropdownIndicator: null,
  Control: CustomControl,
  Option: CustomOption,
  Input: CustomInput,
  ClearIndicator: CustomClearIndicator,
};

export const AddressSelect = ({
  id = null,
  label = null,
  onChange = null,
  onStopEditing = null,
  getAddressComplete,
  getAddressDetails,
  value = '',
  initMenu = false,
  initValue = '',
  ref,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(initMenu);

  const [options, setOptions] = useState([]);

  const handleAddressSearch = useOptimizedSearch(
    async (query) => {
      let recs;
      if (!query.startsWith('http')) {
        recs = await getAddressComplete(query);
      }

      const opts = [{value: query, label: query, type: 'default'}, {type: 'separator'}];

      for (const rec of recs || []) {
        opts.push({
          value: rec.place_id,
          name: rec.name,
          address: rec.address,
          label: rec.address,
        });
      }
      setOptions(opts);
    },
    DEBOUNCE_MS,
    THROTTLE_MS
  );

  const blurring = useRef(false); // Set in onBlur, used in handleInputChange. Doesn't work if done with useState

  const filterOption = useCallback(() => true, []); // Always return true to show all options

  const handleBlur = useCallback(async () => {
    blurring.ref = true;
  }, []);

  const handleChange = useCallback(
    async (option) => {
      setSelectedOption(option ?? null);
      if (!onChange || !option) {
        setInputValue('');
        setOptions([]);
        return;
      }

      if (option.type === 'default') {
        if (option.value.startsWith('http')) onChange({meetingUrl: option.value});
        else onChange({location: option.value});
        setInputValue(option.value);
      } else {
        const results = await getAddressDetails(option.value);
        const data = {...results};
        onChange({address: data});

        if (data.name === data.street) data.name = '';
        const allFields = [data.name, data.street, data.city, data.state, data.zip];
        const fields = [];
        for (const field of allFields) {
          if (field) fields.push(field);
        }
        setInputValue(fields.join(' '));
      }
    },
    [onChange, getAddressDetails]
  );

  const handleInputChange = useCallback(
    async (newValue, actionMeta) => {
      if (actionMeta?.action === 'input-blur') return undefined;
      if (actionMeta?.action === 'menu-close') {
        if (!blurring.ref) {
          setIsMenuOpen(false);
          setInputValue(initValue);
          if (initValue) onStopEditing();
        } else if (!actionMeta.prevInputValue) onChange({clear: true});
        return undefined;
      }

      setInputValue(newValue);
      if (newValue.length > 3) {
        setIsMenuOpen(true);
        handleAddressSearch(newValue);
      } else {
        if (newValue.length === 0) {
          onChange({clear: true});
          setSelectedOption(null);
        }
        setIsMenuOpen(false);
      }
      return newValue;
    },
    [handleAddressSearch, onChange, initValue, onStopEditing]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing functionality
  useEffect(() => {
    if (initMenu) handleInputChange(inputValue);
  }, [initMenu, inputValue]);

  useEffect(() => {
    if (value) {
      setInputValue(value);
      setSelectedOption({value, label: value, type: 'default'});
    } else {
      setInputValue('');
      setSelectedOption(null);
    }
  }, [value]);

  useEffect(() => {
    const sheet = document.querySelectorAll('.bottom-sheet')[0];
    if (sheet) {
      // in MW when this Select is loaded, it receives focus which opens the device keyboard and pushes this out of view.
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 350);
    }
  }, []);

  return (
    <div className="input-wrapper" style={{display: 'block'}}>
      {label && (
        <label className="label2 small input-label" htmlFor={id}>
          {label}
        </label>
      )}
      <Select
        options={options}
        label="Location"
        styles={customStyles}
        components={AddressSelectComponents}
        noOptionsMessage={NoOptions}
        inputValue={inputValue}
        value={selectedOption ? {label: '', value: ''} : null}
        onInputChange={handleInputChange}
        menuIsOpen={isMenuOpen}
        filterOption={filterOption}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=""
        isClearable // Optional: Allow clearing the selected value
        ref={ref}
      />
    </div>
  );
};
